import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import SEO from "@/components/SEO";
import Footer from "@/components/Footer";
import { Radio, RefreshCw, AlertTriangle, Clock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Widget() {
  const [html, setHtml] = useState(null);
  const [uploadedAt, setUploadedAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchWidget = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API}/widget`);
      if (res.status === 404) { setError("not_uploaded"); return; }
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setHtml(data.html_content);
      setUploadedAt(data.uploaded_at);
    } catch {
      setError("fetch_failed");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchWidget(); }, []);

  const formatDate = (iso) => {
    if (!iso) return "--";
    try {
      return new Date(iso).toLocaleString("en-US", {
        month: "short", day: "numeric", year: "numeric",
        hour: "2-digit", minute: "2-digit", timeZoneName: "short",
      });
    } catch { return iso; }
  };

  return (
    <div className="min-h-screen bg-[#F5F0E8] flex flex-col">
      <SEO
        title="QSO Activity — AD0K | Donald Inbody"
        description="Club Log QSO activity stats and contact log for amateur radio station AD0K operated by Donald Inbody."
      />
      <Navbar />

      {/* Header */}
      <section className="bg-[#0A1628] border-b border-[#C5A059]/30 py-10 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center gap-3 mb-1">
            <Radio className="w-5 h-5 text-[#C5A059]" />
            <Link to="/ad0k" className="text-[#C5A059]/70 text-sm tracking-widest uppercase hover:text-[#C5A059] transition-colors">
              AD0K Radio
            </Link>
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-[#F5F0E8] tracking-tight" style={{ fontFamily: "Playfair Display, serif" }}>
            QSO Activity
          </h1>
          <p className="mt-2 text-[#F5F0E8]/60 text-sm max-w-xl">
            Club Log contact statistics and QSO activity for AD0K
          </p>
          {uploadedAt && (
            <div className="mt-3 flex items-center gap-2 text-[#F5F0E8]/40 text-xs">
              <Clock className="w-3.5 h-3.5" />
              <span>Last updated: {formatDate(uploadedAt)}</span>
            </div>
          )}
        </div>
      </section>

      {/* Content */}
      <main className="flex-1 w-full">
        {loading && (
          <div className="flex flex-col items-center justify-center py-32 gap-4">
            <RefreshCw className="w-8 h-8 text-[#0A1628]/40 animate-spin" />
            <p className="text-[#0A1628]/50 text-sm tracking-wide">Loading QSO activity…</p>
          </div>
        )}

        {!loading && error === "not_uploaded" && (
          <div className="flex flex-col items-center justify-center py-32 gap-4 px-6 text-center">
            <div className="w-16 h-16 rounded-full bg-[#0A1628]/10 flex items-center justify-center">
              <Radio className="w-7 h-7 text-[#0A1628]/40" />
            </div>
            <h2 className="text-xl font-semibold text-[#0A1628]" style={{ fontFamily: "Playfair Display, serif" }}>
              Widget Not Yet Uploaded
            </h2>
            <p className="text-[#2D3142]/60 text-sm max-w-sm">
              The QSO activity widget hasn't been uploaded yet. Run the upload script on your Windows machine to publish your stats here.
            </p>
            <Link to="/ad0k" className="mt-2 text-[#C5A059] text-sm font-medium hover:underline">
              ← Back to AD0K Radio
            </Link>
          </div>
        )}

        {!loading && error === "fetch_failed" && (
          <div className="flex flex-col items-center justify-center py-32 gap-4 px-6 text-center">
            <AlertTriangle className="w-8 h-8 text-amber-500" />
            <p className="text-[#0A1628] font-medium">Failed to load widget.</p>
            <button
              onClick={fetchWidget}
              className="px-4 py-2 bg-[#0A1628] text-[#F5F0E8] text-sm rounded hover:bg-[#0A1628]/80 transition-colors"
            >
              Try Again
            </button>
          </div>
        )}

        {!loading && !error && html && (
          <iframe
            data-testid="widget-iframe"
            srcDoc={html}
            title="QSO Activity Widget"
            className="w-full border-none"
            style={{ minHeight: "90vh", height: "100%" }}
            sandbox="allow-scripts allow-same-origin"
          />
        )}
      </main>

      <Footer />
    </div>
  );
}
